// App.jsx
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import { useAuth, AuthProvider } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";

// Public pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import TrackingPage from "./pages/TrackingPage";
import PendingApprovalPage from "./pages/PendingApprovalPage";
import SuperAdminPendingShopsPage from "./pages/SuperAdminPendingShopsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TrialExpiredPage from "./pages/TrialExpiredPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFoundPage from "./pages/NotFoundPage";

// Shop admin pages
import DashboardPage from "./pages/DashboardPage";
import OrdersPage from "./pages/OrdersPage";
import RidersPage from "./pages/RidersPage";
import MapPage from "./pages/MapPage";
import ContactPage from "./pages/ContactPage";
import SettingsPage from "./pages/SettingsPage";

// Super admin pages
import SuperAdminDashboardPage from "./pages/SuperAdminDashboardPage";
import SuperAdminShopsPage from "./pages/SuperAdminShopsPage";
import SuperAdminMessagesPage from "./pages/SuperAdminMessagesPage";
import SuperAdminPaymentsPage from "./pages/SuperAdminPaymentsPage";
import SuperAdminShopDetailPage from "./pages/SuperAdminShopDetailPage";

function PrivateRoute({ children }) {
  const { token, user } = useAuth();

  if (!token) return <Navigate to="/login" replace />;

  if (user?.role === "super_admin") return children;

  if (user?.shop?.status === "pending") {
    return <Navigate to="/pending-approval" replace />;
  }

  if (user?.shop?.status === "rejected") {
    return <Navigate to="/login" replace />;
  }

  if (user?.shop?.is_active === false) {
    return <Navigate to="/pending-approval" replace />;
  }

  const trialEndsAt = user?.shop?.trial_ends_at;
  if (trialEndsAt && new Date(trialEndsAt) < new Date()) {
    return <Navigate to="/trial-expired" replace />;
  }

  return children;
}

function TrialExpiredRoute({ children }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { token, user } = useAuth();

  if (!token) return children;
  if (user?.role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (user?.shop?.status === "pending") return <Navigate to="/pending-approval" replace />;
  return <Navigate to="/dashboard" replace />;
}

function PendingRoute({ children }) {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function SuperAdminRoute({ children }) {
  const { token, user } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/track/:token" element={<TrackingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route
          path="/login"
          element={
            <GuestRoute>
              <LoginPage />
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <RegisterPage />
            </GuestRoute>
          }
        />
        <Route
          path="/pending-approval"
          element={
            <PendingRoute>
              <PendingApprovalPage />
            </PendingRoute>
          }
        />
        <Route
          path="/trial-expired"
          element={
            <TrialExpiredRoute>
              <TrialExpiredPage />
            </TrialExpiredRoute>
          }
        />

        {/* Shop admin */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/orders"
          element={
            <PrivateRoute>
              <OrdersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/riders"
          element={
            <PrivateRoute>
              <RidersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/map"
          element={
            <PrivateRoute>
              <MapPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <SettingsPage />
            </PrivateRoute>
          }
        />

        {/* Super admin */}
        <Route
          path="/super-admin"
          element={
            <SuperAdminRoute>
              <SuperAdminDashboardPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/shops"
          element={
            <SuperAdminRoute>
              <SuperAdminShopsPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/shops/:id"
          element={
            <SuperAdminRoute>
              <SuperAdminShopDetailPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/businesses"
          element={
            <SuperAdminRoute>
              <SuperAdminPendingShopsPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/payments"
          element={
            <SuperAdminRoute>
              <SuperAdminPaymentsPage />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/messages"
          element={
            <SuperAdminRoute>
              <SuperAdminMessagesPage />
            </SuperAdminRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}